<template>
    <header class="navbar-container" :class="{ 'scrolled': scrolled }">
        <div class="inner relative lg:h-[72px]">
            <div class="flex items-center px-4 lg:px-0 py-3 lg:py-0 border-b lg:border-b-0 border-[#E4E4E4] h-[64px] lg:h-auto">
                <NuxtLink to="/" class="cursor-pointer">
                    <img fetchpriority="high" width="129" height="28" :src="$image('logo-default.svg')" alt="Peekage Logo">
                </NuxtLink>
                <div class="hidden lg:flex items-center gap-4 lg:ml-10">
                    <UNavigationMenu
                        v-model="activeTab"
                        target="click"
                        :ui="{
                            root: '[position:unset]!',
                            list: 'p-0 m-0 gap-4! inset-0',
                            content: 'bg-white rounded-lg max-h-[unset]!',
                            viewport: 'ring-0! border-0! bg-white rounded-[20px] absolute top-[3px] max-h-[unset]!',
                            item: 'py-[18px] font-manrope tracking-[-0.3px] max-h-[unset]',
                            link: 'text-black text-base not-italic font-medium leading-[20px] tracking-[-0.3px] rounded-md transition-colors hover:bg-[var(--color-primary-100)] relative after:absolute after:bottom-[-20px] after:left-0 after:right-0 after:h-[2px] hover:after:bg-[#FF647F] data-[state=open]:after:bg-[#FF647F] pl-4 py-2 pr-2 cursor-pointer',
                        }"
                        :items="mainItems"
                    >
                    <template #platform-content>
                        <NavItem
                            :sub-menus="platformMenu.sub" 
                            :menu-version="platformMenu.version" 
                            :menu-name="platformMenu.name"
                        />
                    </template>
                    <template #solutions-content>
                        <NavItem 
                            :sub-menus="solutionsMenu.sub" 
                            :menu-version="solutionsMenu.version" 
                            :menu-name="solutionsMenu.name"
                        />
                    </template>
                    </UNavigationMenu>
                    
                    <UNavigationMenu
                        v-model="resourcesActiveTab"
                        :ui="{
                            root: '!w-[300px]',
                            list: 'p-0 m-0 !gap-4 inset-0 !top-4',
                            content: 'bg-white rounded-lg ![max-height:unset]',
                            viewport: '!ring-0 !border-0 bg-white rounded-[20px] !top-0.5',
                            item: 'py-[18px] font-manrope tracking-[-0.3px]',
                            link: 'text-black text-base not-italic font-medium leading-[20px] tracking-[-0.3px] rounded-md transition-colors hover:bg-[var(--color-primary-100)] relative after:absolute after:bottom-[-20px] after:left-0 after:right-0 after:h-[2px] hover:after:bg-[#FF647F] data-[state=open]:after:bg-[#FF647F] !ring-0 pl-4 py-2 pr-2 cursor-pointer',
                        }"
                        :items="resourcesItems"
                    >
                    <template #resources-content>
                        <div class="!w-[100px] relative">
                            <NavItem
                                :sub-menus="resourcesMenu.sub" 
                                :menu-version="resourcesMenu.version" 
                                :menu-name="resourcesMenu.name"
                            />
                        </div>
                    </template>
                    </UNavigationMenu>
                </div>
                
                <!-- Mobile Menu Button -->
                <button
                    type="button"
                    class="lg:hidden w-8 h-8 p-0 ml-auto relative flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                    :aria-label="mobileMenuOpen ? 'Close menu' : 'Open menu'"
                    @click="mobileMenuOpen = !mobileMenuOpen; mobileActiveMenu = null"
                >
                    <Transition name="icon-rotate" mode="out-in">
                        <UIcon
                            :name="mobileMenuOpen ? 'i-lucide-x' : 'i-pkg-navigation-menu'"
                            class="w-8 h-8 transition-transform duration-300 shrink-0"
                        />
                    </Transition>
                </button>
                <UButton
                    to="/contact?talk-to-an-expert=true"
                    color="primary"
                    variant="solid"
                    class="tracking-[-0.3px] font-manrope bg-primary-600 text-[14px] leading-4 font-bold text-white no-underline hidden lg:block px-3 py-[10px] lg:ml-auto"
                    label="Talk to an expert"
                />
            </div>
            <div v-if="mobileMenuOpen" class="lg:hidden bg-white py-4 absolute top-full left-0 right-0 z-50 max-h-[calc(100vh-64px)] overflow-y-auto">
                <!-- Main Menu Items (shown when mobileActiveMenu is null) -->
                <template v-if="!mobileActiveMenu">
                    <template v-for="(item, index) in items" :key="index">
                        <div 
                            v-if="item.slot" 
                            class="flex items-center justify-between w-full py-4 px-4 cursor-pointer hover:bg-gray-50 transition-colors"
                            @click="mobileActiveMenu = item.slot"
                        >
                            <span class="font-bold font-manrope text-lg leading-6 tracking-[-0.3px] text-[#141414]">{{ item.label }}</span>
                            <UIcon name="i-pkg-navigation-chevron-right" class="size-8 shrink-0 text-[#141414]" />
                        </div>
                        <NuxtLink 
                            v-else-if="item.to"
                            :to="item.to"
                            class="flex font-manrope items-center w-full py-4 px-4 font-bold text-lg leading-6 tracking-[-0.3px] hover:bg-gray-50 transition-colors !no-underline text-[#141414] cursor-pointer"
                            @click="mobileMenuOpen = false; mobileActiveMenu = null"
                        >
                            {{ item.label }}
                        </NuxtLink>
                        <div v-if="index < items.length - 1" class="h-[2px] w-[96%] mx-auto my-2 bg-[#F4F4F4]" />
                    </template>
                    <div class="h-[2px] w-[96%] mx-auto my-2 bg-[#F4F4F4]" />
                    <div class="py-4 px-5 flex flex-col gap-1 min-[400px]:flex-row min-[400px]:items-center min-[400px]:gap-12">
                        <span class="font-semibold font-manrope text-base leading-4 text-[#A4A4A4] w-fit whitespace-nowrap">Need help?</span>
                        <UButton
                            to="/contact?talk-to-an-expert=true"
                            color="primary"
                            variant="solid"
                            size="sm"
                            class="tracking-[-0.3px] font-manrope bg-primary-600 text-[14px] leading-4 font-bold text-white no-underline w-full min-[400px]:grow-1 flex items-center justify-center px-3 py-[10px] style-none"  
                            label="Talk to an expert"
                        />
                    </div>
                </template>
                
                <!-- Submenu Content (shown when mobileActiveMenu is set) -->
                <template v-else>
                    <!-- Submenu Content -->
                    <div class="mobile-submenu-content" @click="handleMobileSubmenuClick">
                        <NavItem
                            v-if="mobileActiveMenu === 'platform'"
                            :sub-menus="platformMenu.sub" 
                            :menu-version="platformMenu.version" 
                            :menu-name="platformMenu.name"
                            @close-item="handleCloseItem"
                        />
                        <NavItem
                            v-else-if="mobileActiveMenu === 'solutions'"
                            :sub-menus="solutionsMenu.sub" 
                            :menu-version="solutionsMenu.version" 
                            :menu-name="solutionsMenu.name"
                            @close-item="handleCloseItem"
                        />
                        <NavItem
                            v-else-if="mobileActiveMenu === 'resources'"
                            :sub-menus="resourcesMenu.sub" 
                            :menu-version="resourcesMenu.version" 
                            :menu-name="resourcesMenu.name"
                            @close-item="handleCloseItem"
                        />
                    </div>
                </template>
            </div>
        </div>
    </header>
</template>

<script>
import NavItem from '~/components/layout/header/nav-item';

const _items = [
  {
    label: 'Platform',
    slot: 'platform',
    trailingIcon: 'i-pkg-navigation-chevron-down',
  },
  {
    label: 'Solutions',
    slot: 'solutions',
    trailingIcon: 'i-pkg-navigation-chevron-down',
  },
  {
    label: 'Consumer App',
    to: '/consumer-apps',
  },
  {
    label: 'Resources',
    slot: 'resources',
    trailingIcon: 'i-pkg-navigation-chevron-down',
  }
]


const menus = [
    // {
    //     name: 'Why Peekage ?',
    //     component: 'PeekageNavItem',
    //     uri: '/#WhyPeekage',
    //     anchored: true,
    // },
    {
        name: 'Platform',
        component: 'PeekageNavGroup',
        version: 'Style1',
        sub: [
            {
                title: 'Consumer Insight',
                subTitle: 'Peekage empowers brands to integrate actionable insights across their business with its automated product and concept testing solutions.',
                imgPath: 'header/consumerInsight.png',
                path: '/consumer-insights-company',
                type: 'NAV_ITEM',
            },
            {
                title: 'Shopper Marketing',
                subTitle: "Peekage's digital product sampling programs let you engage qualified shoppers through targeted in-home trials. Use the Audience Builder to create specific segments, matching your products to shoppers for reviews, awareness, and sales.",
                path: '/product-sampling-program',
                imgPath: 'header/shopperMarketing.png',
                type: 'NAV_ITEM',
            },
            {
                title: 'Why Peekage?',
                subTitle: 'Access a diverse community of 5M+ shoppers and drive retail velocity with geo-targeting. Get live consumer data in days, not weeks, with affordable, all-in-one product testing solutions that are easy to use, requiring no data science expertise.',
                imgPath: 'header/shopperMarketing.png',
                type: 'SIDE_ITEM',
            },
        ],
    },
    {
        name: 'Solutions',
        component: 'PeekageNavGroup',
        version: 'Style2',
        sub: [
            {
                name: 'Consumer Insights',
                subtitle: 'Consumer Insight Use Cases',
                identifier: 'consumer-insights-company',
                useCases: [
                    {
                        case: 'Concept Testing',
                        uri: '/concept-testing',
                    },
                    {
                        case: 'Taste & Formula Testing',
                        uri: '/taste-sensory-testing',
                    },
                    {
                        case: 'Buyer Personas & Segmentation',
                        uri: '/customer-segmentation',
                    },
                    {
                        case: 'In-home Usage Testing (IHUT)',
                        uri: '/in-home-product-testing',
                    },
                    {
                        case: 'Efficacy & Claims Testing',
                        uri: '/claims-testing',
                    },
                    {
                        case: 'Packaging Testing',
                        uri: '#',
                    },
                    {
                        case: 'Pricing',
                        uri: '#',
                    },
                    {
                        case: 'Messaging & Positioning',
                        uri: '#',
                    },
                    {
                        case: 'Competitive Analysis',
                        uri: '#',
                    },
                    {
                        case: 'Brand Perception',
                        uri: '#',
                    },
                    {
                        case: 'In-Store Experience',
                        uri: '#',
                    },
                    {
                        case: 'Voice of the Customer (VoC)',
                        uri: '#',
                    },
                    {
                        case: 'Usage and Attitude (U&A)',
                        uri: '/usage-and-attitude-testing-services',
                    },
                ],
                uri: '/consumer-insights-company',
                anchored: false,
            },
            {
                name: 'Digital Product Sampling',
                subtitle: 'Digital Product Sampling Use Cases',
                identifier: 'product-sampling-program',
                useCases: [
                    {
                        case: 'At-home Product Sampling',
                        uri: '#',
                    },
                    {
                        case: 'Increase Retail Velocity with Cashback',
                        uri: '#',
                    },
                    {
                        case: 'Ratings & Reviews',
                        uri: '#',
                    },
                ],
                uri: '/product-sampling-program',
                anchored: false,
            },
            {
                name: 'By Industries',
                subtitle: 'Industries Use Cases',
                identifier: 'industries',
                useCases: [
                    {
                        case: 'Food & Beverage',
                        uri: '#',
                    },
                    {
                        case: 'Pet Products',
                        uri: '#',
                    },
                    {
                        case: 'Household Products',
                        uri: '#',
                    },
                    {
                        case: 'Personal Care & Beauty',
                        uri: '#',
                    },
                    {
                        case: 'Health & Wellness',
                        uri: '#',
                    },
                ],
                uri: '/product-sampling-program',
                anchored: false,
            },
        ],
    },
    {
        name: 'Consumer App',
        component: 'PeekageNavItem',
        uri: '/consumer-apps',
        anchored: false,
    },
    {
        name: 'Resources',
        version: 'Style3',
        component: 'PeekageNavGroup',
        sub: [
            {
                name: 'Blog',
                subtitle: '',
                uri: 'https://peekage.com/blog',
                anchored: false,
            },
            {
                name: 'FAQ',
                subtitle: '',
                uri: '/faq',
                anchored: false,
            },
            {
                name: 'Case Studies',
                subtitle: '',
                uri: '/case-studies',
                anchored: false,
            },
            {
                name: 'Our Community',
                subtitle: '',
                uri: '/natural-shoppers-community',
                anchored: false,
            },
        ],
    },
    {
        name: 'Need help?',
        component: 'TalkToExpert',
        uri: '/contact?talk-to-an-expert=true',
        anchored: false,
        onlyDevice: true,
    },
];
export default {
    components: {
        NavItem,
    },
    data() {
        return {
            items: [..._items],
            menus: [...menus],
            scrolled: false,
            activeTab: 'platform', // Set default open menu: 'platform', 'solutions'
            resourcesActiveTab: 'resources', // Set default open menu for resources
            mobileMenuOpen: false,
            mobileActiveMenu: null, // Tracks which sub-menu is currently shown (null = main menu, 'platform'|'solutions'|'resources' = sub-menu)
        };
    },
    computed: {
        mainItems() {
            // Return all items except Resources (last item)
            return this.items.filter(item => item.label !== 'Resources');
        },
        resourcesItems() {
            // Return only Resources item
            return this.items.filter(item => item.label === 'Resources');
        },
        platformMenu() {
            return this.menus.find(menu => menu.name === 'Platform') || {};
        },
        solutionsMenu() {
            return this.menus.find(menu => menu.name === 'Solutions') || {};
        },
        resourcesMenu() {
            return this.menus.find(menu => menu.name === 'Resources') || {};
        },
    },
    watch: {
        mobileMenuOpen(newVal) {
            // Reset active menu when dropdown closes
            if (!newVal) {
                this.mobileActiveMenu = null;
            }
        },
        '$route'(to, from) {
            if (to.path !== from.path) {
                // Mobile
                if (this.mobileMenuOpen) {
                    this.mobileMenuOpen = false;
                    this.mobileActiveMenu = null;
                }
                // Desktop
                this.activeTab = null;
                this.resourcesActiveTab = null;
            }
        },
    },
    beforeMount() {
        if (import.meta.client) {
            window.addEventListener('scroll', this.handleHeaderStyles);
        }
    },
    beforeUnmount() {
        if (import.meta.client) {
            window.removeEventListener('scroll', this.handleHeaderStyles);
            const layoutEl = document.getElementById('__layout') || document.body;
            layoutEl.removeEventListener('click', this.handleHide);
        }
    },
    mounted() {
        if (import.meta.client) {
            const layoutEl = document.getElementById('__layout') || document.body;
            if (layoutEl) {
                layoutEl.addEventListener('click', this.handleHide);
            }
        }
    },
    methods: {
        handleCloseItem() {
            this.mobileActiveMenu = null;
        },
        handleHide() {
            if (import.meta.client) {
                window.document.querySelectorAll('.peekage-nav-item').forEach((item) => {
                    // if (item.id !== this.menu.name) {
                    item.classList.remove('hidden');
                    // }
                });
            }
        },
        handleHeaderStyles () {
             if (import.meta.client) {
                 this.scrolled = window.scrollY > 0;
             }
         },
        handleMobileSubmenuClick(event) {
            // Close mobile menu when a link is clicked
            const link = event.target.closest('a, nuxt-link, [role="link"]');
            if (link) {
                const href = link.getAttribute('href') || link.getAttribute('to');
                if (href && href !== '#' && !href.startsWith('#')) {
                    this.mobileMenuOpen = false;
                    this.mobileActiveMenu = null;
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
    .header-only-logo {
        .header-talk-to-expert, .nav-menus {
            display: none !important;
            border-bottom: 2px solid #e5e5e5;
            margin-right: 0;
        }
        @media (min-width: 992px){
            margin-right: 28px;
        }
    }

    .navbar-container {
        .inner{
            padding: 0 0;
            max-width: 1164px;
            margin: 0 auto;
            .brand{
                margin-left: 20px;
                padding: 0;
            }
            .toggler{
                margin-right: 20px;
            }
        }
    
        border-bottom:2px solid #E5E5E5;
        .outline-none{
            outline: none;
        }
        position: sticky;
        top: 0px;
        width: 100%;
        z-index: 1000;
        background-color: #fff;
        box-shadow: none;
        &.scrolled {
            box-shadow: 0px 1px 8px 0px rgba(0, 0, 0, 0.04), 0px 4px 8px 0px rgba(0, 0, 0, 0.12);
        }

        @media (min-width: 992px) {

            .inner{
                    padding: 0 28px;
                    .brand{
                        margin-left: 0;
                    }
                    .toggler{
                        margin-right: 0;
                    }
                }

                border-bottom: none;

            border-bottom: unset;
            position: sticky;
            .navbar-desktop {
                border-top: 2px solid red;
                margin-left: 40px;
                gap: 16px;
            }
        }
        @media (min-width: 1164px) {
            .navbar-desktop {
                border-top: unset;
            }    
            .inner{
                padding: 0 0;
                .brand{
                    margin-left: 0;
                }
                .toggler{
                    margin-right: 0;
                }
            }
        }
    }

    .mobile-submenu-content {
        max-height: calc(100vh - 200px);
        overflow-y: auto;
    }

    /* Icon rotation transition */
    .icon-rotate-enter-active,
    .icon-rotate-leave-active {
        transition: transform 0.3s ease, opacity 0.2s ease;
    }

    .icon-rotate-enter-from {
        opacity: 0;
        transform: rotate(-90deg) scale(0.8);
    }

    .icon-rotate-enter-to {
        opacity: 1;
        transform: rotate(0deg) scale(1);
    }

    .icon-rotate-leave-from {
        opacity: 1;
        transform: rotate(0deg) scale(1);
    }

    .icon-rotate-leave-to {
        opacity: 0;
        transform: rotate(90deg) scale(0.8);
    }
</style>
